/**
 * SkillMenu - Shared menu content for skill actions
 *
 * Used by:
 * - SkillsListPanel (dropdown via "..." button, context menu via right-click)
 * - SkillInfoPage (title dropdown menu)
 *
 * Uses MenuComponents context to render with either DropdownMenu or ContextMenu
 * primitives, allowing the same component to work in both scenarios.
 *
 * Provides consistent skill actions:
 * - Open in New Window
 * - Show in file manager
 * - Delete
 */

import * as React from 'react'
import {
  Trash2,
  FolderOpen,
  AppWindow,
  Download,
  Pencil,
} from 'lucide-react'
import { useMenuComponents } from '@/components/ui/menu-context'
import { isIPad, getFileManagerName } from '@/lib/platform'

export interface SkillMenuProps {
  /** Skill slug */
  skillSlug: string
  /** Skill name for display */
  skillName: string
  /** Whether this is a built-in default skill that cannot be deleted */
  isDefault?: boolean
  /** Callbacks */
  onOpenInNewWindow: () => void
  onShowInFinder: () => void
  onExport: () => void
  onRename?: () => void
  onDelete: () => void
}

/**
 * SkillMenu - Renders the menu items for skill actions
 * This is the content only, not wrapped in a DropdownMenu or ContextMenu
 */
export function SkillMenu({
  skillSlug,
  skillName,
  isDefault,
  onOpenInNewWindow,
  onShowInFinder,
  onExport,
  onRename,
  onDelete,
}: SkillMenuProps) {
  // Get menu components from context (works with both DropdownMenu and ContextMenu)
  const { MenuItem, Separator } = useMenuComponents()

  return (
    <>
      {/* Open in New Window — desktop only */}
      {!isIPad && (
        <MenuItem onClick={onOpenInNewWindow}>
          <AppWindow className="h-3.5 w-3.5" />
          <span className="flex-1">Open in New Window</span>
        </MenuItem>
      )}

      {/* Show in file manager — desktop only */}
      {!isIPad && (
        <MenuItem onClick={onShowInFinder}>
          <FolderOpen className="h-3.5 w-3.5" />
          <span className="flex-1">{`Show in ${getFileManagerName()}`}</span>
        </MenuItem>
      )}

      {/* Export as zip — desktop only */}
      {!isIPad && (
        <MenuItem onClick={onExport}>
          <Download className="h-3.5 w-3.5" />
          <span className="flex-1">Export as Zip</span>
        </MenuItem>
      )}

      {/* Rename — hidden for default (built-in) skills */}
      {!isDefault && onRename && (
        <>
          <Separator />
          <MenuItem onClick={onRename}>
            <Pencil className="h-3.5 w-3.5" />
            <span className="flex-1">Rename</span>
          </MenuItem>
        </>
      )}

      {/* Delete — hidden for default (built-in) skills */}
      {!isDefault && (
        <>
          {!(onRename) && <Separator />}
          <MenuItem onClick={onDelete} variant="destructive">
            <Trash2 className="h-3.5 w-3.5" />
            <span className="flex-1">Delete Skill</span>
          </MenuItem>
        </>
      )}
    </>
  )
}
